import React, { useEffect, useMemo, useState } from 'react';

import { Badge, Group, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import Menu from 'components/Menu';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useDocuments from 'hooks/document/useDocuments';
import { CellProps } from 'react-table';
import { DocumentList } from 'types';
import { removeEmpty } from 'utils/functions';

import DeleteIssuedDocumentModal from './DeleteIssuedDocumentModal';
import DocumentSignModal from '../../AthleteDetail/components/Documents/components/DocumentSignModal';
import { IssuedDocumentSearchFilters } from '../types';

const IssuedDocumentsTable = ({
  searchFilters,
  permission = true,
}: {
  searchFilters: IssuedDocumentSearchFilters;
  permission?: boolean;
}): JSX.Element => {
  const { dateFormat } = useLocalization();

  const [tableData, setTableData] = useState<DocumentList[]>([]);
  const [ordering, setOrdering] = useState('firstName');
  const [ascending, setAscending] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState<DocumentList>();
  const [isDeleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [isSignModalOpened, { toggle: handleSignModalToggle }] = useDisclosure(false);

  const orderingValue = useMemo(() => {
    if (ordering === 'firstName') {
      if (searchFilters.isArrangeByLastName) {
        return 'athlete__lastName';
      }
      return 'athlete__firstName';
    }
    if (ordering === 'status') {
      return 'signed_date';
    }
    return ordering;
  }, [ordering, searchFilters.isArrangeByLastName]);

  const strippedFilters = removeEmpty(searchFilters);
  if ('isSigned' in strippedFilters) {
    strippedFilters.isSigned = strippedFilters.isSigned === 'True';
  }
  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useDocuments({
      ...strippedFilters,
      ordering: ascending ? orderingValue : `-${orderingValue}`,
      isTemplate: false,
    });

  const handleCloseSignModal = (): void => {
    handleSignModalToggle();
    setSelectedDocument(undefined);
  };

  const handleHeaderClick = (header: string): void => {
    setOrdering(header);
    setAscending(a => !a);
  };

  const handleDelete = (document: DocumentList): void => {
    setSelectedDocument(document);
    openDeleteModal();
  };

  const handleDescriptionClick = (document: DocumentList): void => {
    setSelectedDocument(document);
    handleSignModalToggle();
  };

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  const columns = [
    {
      Header: 'Athlete Name',
      accessor: 'firstName' as const,
      Cell: ({ row }: CellProps<DocumentList>) =>
        row.original.athlete?.fullName ? (
          <Text weight='bold' underline sx={{ cursor: 'pointer', width: 'fit-content' }}>
            {row.original.athlete?.fullName}
          </Text>
        ) : (
          '-'
        ),
    },
    {
      Header: 'Description',
      accessor: 'description' as const,
      Cell: ({ row }: CellProps<DocumentList>) => (
        <Text
          weight='bold'
          fs='italic'
          underline
          sx={{ cursor: 'pointer', width: 'fit-content' }}
          onClick={() => handleDescriptionClick(row.original)}
          data-cy='documentDescription'
        >
          {row.original.description}
        </Text>
      ),
    },
    {
      Header: 'Type',
      accessor: 'type' as const,
    },
    {
      Header: 'Status',
      accessor: 'status' as const,
      Cell: ({ row }: CellProps<DocumentList>) => (
        <Badge
          color={row.original.signedDate ? 'green' : 'red'}
          variant='filled'
          size='xs'
          fullWidth
          maw={75}
          data-cy='signedStatus'
        >
          {row.original.signedDate ? 'Signed' : 'Not Signed'}
        </Badge>
      ),
    },
    {
      Header: 'Date Signed',
      accessor: 'signedDate' as const,
      Cell: ({ value }: CellProps<DocumentList>) => (value ? dayjs(value).format(dateFormat) : '-'),
    },
    {
      id: 'actions',
      Cell: ({ row }: CellProps<DocumentList>) => {
        const items =
          row.original.signedDate === null
            ? [
                {
                  title: 'View',
                  onClick: () => handleDescriptionClick(row.original),
                  'data-cy': 'viewButton',
                },
                {
                  title: 'Delete',
                  onClick: () => handleDelete(row.original),
                  color: 'red',
                  'data-cy': 'deleteButton',
                },
              ]
            : [
                {
                  title: 'View',
                  onClick: () => handleDescriptionClick(row.original),
                  'data-cy': 'viewButton',
                },
              ];

        return (
          <Group position='right' px='xs'>
            <Menu position='bottom-end' items={items} disabled={!permission} data-cy='documentsMenuButton' />
          </Group>
        );
      },
      minWidth: '50px',
      maxWidth: '50px',
      sticky: true,
    },
  ];

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['firstName', 'description', 'status', 'signedDate']}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
        ascending={ascending}
      />
      {selectedDocument && (
        <DeleteIssuedDocumentModal
          document={selectedDocument}
          opened={isDeleteModalOpen}
          closeHandler={closeDeleteModal}
        />
      )}

      {selectedDocument?.athlete && selectedDocument?.documentId && (
        <DocumentSignModal
          opened={isSignModalOpened}
          documentId={selectedDocument.documentId}
          closeHandler={handleCloseSignModal}
          permission={permission}
        />
      )}
    </>
  );
};

export default IssuedDocumentsTable;
